/**
 * Livro-razão da obra — projeto_sistema_obras.md §4.4 (ADR 010).
 *
 * Toda alteração financeira ou de prazo passa por aqui. São funções puras:
 * recebem o estado atual da obra e a movimentação pretendida, devolvem o
 * lançamento e o próximo estado. Não tocam banco, relógio nem rede.
 *
 * Isso permite que a mesma regra seja usada pela API, pelo script de migração
 * do sistema antigo e pela fila offline do PWA, sem risco de divergirem.
 */

import { REPORT_TYPE_META, type ReportType } from './domain.js';
import type { Cents } from './format.js';

/** Situação financeira e de prazo da obra em um instante. */
export interface ObraLedgerState {
  readonly currentValueCents: Cents;
  readonly paidValueCents: Cents;
  readonly glossValueCents: Cents;
  readonly contractEnd: string | null;
  readonly osEnd: string | null;
}

export interface MovementInput {
  readonly type: ReportType;
  /** Valor informado pelo usuário, sempre positivo. Ignorado em `visita`. */
  readonly amountCents?: Cents | undefined;
  /** Dias informados nos aditivos de prazo, sempre positivo. */
  readonly days?: number | undefined;
  /** Data do ato administrativo (`movement_date`), não a data da visita. */
  readonly effectiveDate: string;
}

/** Lançamento gravado no razão. Nunca é editado nem excluído — só estornado. */
export interface MovementEntry {
  readonly type: ReportType;
  /** Com sinal: supressão é negativa. Zero em movimentações só de prazo. */
  readonly amountCents: Cents;
  readonly days: number | null;
  readonly effectiveDate: string;
  readonly previousValueCents: Cents;
  readonly newValueCents: Cents;
  readonly previousPaidCents: Cents;
  readonly newPaidCents: Cents;
  readonly previousGlossCents: Cents;
  readonly newGlossCents: Cents;
  readonly previousEndDate: string | null;
  readonly newEndDate: string | null;
}

export interface MovementResult {
  readonly entry: MovementEntry;
  readonly nextState: ObraLedgerState;
}

export const LEDGER_VIOLATIONS = [
  'AMOUNT_REQUIRED',
  'AMOUNT_MUST_BE_POSITIVE',
  'DAYS_REQUIRED',
  'DAYS_MUST_BE_POSITIVE',
  'EXCEEDS_CONTRACT_BALANCE',
  'BELOW_COMMITTED_VALUE',
  'NO_BALANCE_TO_GLOSS',
  'MISSING_BASE_DATE',
] as const;

export type LedgerViolation = (typeof LEDGER_VIOLATIONS)[number];

export const LEDGER_VIOLATION_MESSAGES: Record<LedgerViolation, string> = {
  AMOUNT_REQUIRED: 'Informe o valor da movimentação.',
  AMOUNT_MUST_BE_POSITIVE: 'O valor precisa ser maior que zero.',
  DAYS_REQUIRED: 'Informe a quantidade de dias.',
  DAYS_MUST_BE_POSITIVE: 'A quantidade de dias precisa ser maior que zero.',
  EXCEEDS_CONTRACT_BALANCE: 'O valor ultrapassa o saldo disponível do contrato.',
  BELOW_COMMITTED_VALUE: 'A supressão deixaria o contrato abaixo do que já foi pago ou glosado.',
  NO_BALANCE_TO_GLOSS: 'Não há saldo remanescente para glosar.',
  MISSING_BASE_DATE: 'A obra não tem a data de fim correspondente para receber o aditivo de prazo.',
};

export class LedgerError extends Error {
  constructor(
    readonly violation: LedgerViolation,
    /** Campo do formulário a destacar. */
    readonly field: 'amountCents' | 'days',
  ) {
    super(LEDGER_VIOLATION_MESSAGES[violation]);
    this.name = 'LedgerError';
  }
}

/** Saldo ainda não pago nem glosado. É o teto de uma medição e o valor de uma glosa. */
export function remainingBalanceCents(state: ObraLedgerState): Cents {
  return state.currentValueCents - state.paidValueCents - state.glossValueCents;
}

/**
 * O contrato ainda cobre o que já foi pago e glosado.
 *
 * `applyMovement` guarda isso na supressão; o estorno não guarda, porque é a
 * conta que desfaz outra. Quem corrige um relatório (ADR 060) confere aqui antes
 * de gravar: tirar um aditivo cujo valor já foi medido deixaria o pago acima do
 * contrato.
 */
export function coversCommitted(state: ObraLedgerState): boolean {
  return state.currentValueCents >= state.paidValueCents + state.glossValueCents;
}

/** Soma dias a uma data-calendário `yyyy-mm-dd`, sem envolver fuso horário. */
export function addDays(date: string, days: number): string {
  const base = new Date(`${date}T00:00:00.000Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

function requirePositiveAmount(input: MovementInput): Cents {
  if (input.amountCents == null) throw new LedgerError('AMOUNT_REQUIRED', 'amountCents');
  if (input.amountCents <= 0) throw new LedgerError('AMOUNT_MUST_BE_POSITIVE', 'amountCents');
  return input.amountCents;
}

function requirePositiveDays(input: MovementInput): number {
  if (input.days == null) throw new LedgerError('DAYS_REQUIRED', 'days');
  if (input.days <= 0) throw new LedgerError('DAYS_MUST_BE_POSITIVE', 'days');
  return input.days;
}

function baseEntry(state: ObraLedgerState, input: MovementInput): MovementEntry {
  return {
    type: input.type,
    amountCents: 0,
    days: null,
    effectiveDate: input.effectiveDate,
    previousValueCents: state.currentValueCents,
    newValueCents: state.currentValueCents,
    previousPaidCents: state.paidValueCents,
    newPaidCents: state.paidValueCents,
    previousGlossCents: state.glossValueCents,
    newGlossCents: state.glossValueCents,
    previousEndDate: null,
    newEndDate: null,
  };
}

/**
 * Aplica uma movimentação ao estado da obra.
 *
 * Lança `LedgerError` quando a regra de negócio é violada — o chamador traduz
 * para erro de validação por campo.
 */
export function applyMovement(state: ObraLedgerState, input: MovementInput): MovementResult {
  const base = baseEntry(state, input);

  switch (input.type) {
    case 'visita': {
      // Sem efeito financeiro nem de prazo: nada muda.
      return { entry: base, nextState: state };
    }

    case 'medicao': {
      const amount = requirePositiveAmount(input);

      // Medição não pode pagar mais do que o contrato ainda deve.
      if (amount > remainingBalanceCents(state)) {
        throw new LedgerError('EXCEEDS_CONTRACT_BALANCE', 'amountCents');
      }

      const newPaid = state.paidValueCents + amount;
      return {
        entry: { ...base, amountCents: amount, newPaidCents: newPaid },
        nextState: { ...state, paidValueCents: newPaid },
      };
    }

    case 'aditivo_valor': {
      const amount = requirePositiveAmount(input);
      const newValue = state.currentValueCents + amount;

      return {
        entry: { ...base, amountCents: amount, newValueCents: newValue },
        nextState: { ...state, currentValueCents: newValue },
      };
    }

    case 'supressao_valor': {
      const amount = requirePositiveAmount(input);
      const newValue = state.currentValueCents - amount;

      /*
       * O contrato não pode encolher para menos do que já foi pago ou glosado:
       * isso deixaria o razão inconsistente e o percentual pago acima de 100%.
       */
      if (newValue < state.paidValueCents + state.glossValueCents) {
        throw new LedgerError('BELOW_COMMITTED_VALUE', 'amountCents');
      }

      return {
        // Supressão entra no razão com sinal negativo.
        entry: { ...base, amountCents: -amount, newValueCents: newValue },
        nextState: { ...state, currentValueCents: newValue },
      };
    }

    case 'glosa': {
      const balance = remainingBalanceCents(state);
      if (balance <= 0) throw new LedgerError('NO_BALANCE_TO_GLOSS', 'amountCents');

      /*
       * A glosa toma todo o saldo remanescente e encerra o contrato
       * financeiramente — o valor não é escolhido pelo usuário.
       */
      const newGloss = state.glossValueCents + balance;
      return {
        entry: { ...base, amountCents: balance, newGlossCents: newGloss },
        nextState: { ...state, glossValueCents: newGloss },
      };
    }

    case 'aditivo_prazo_contrato': {
      const days = requirePositiveDays(input);
      if (!state.contractEnd) throw new LedgerError('MISSING_BASE_DATE', 'days');

      const newEnd = addDays(state.contractEnd, days);
      return {
        entry: { ...base, days, previousEndDate: state.contractEnd, newEndDate: newEnd },
        nextState: { ...state, contractEnd: newEnd },
      };
    }

    case 'aditivo_prazo_os': {
      const days = requirePositiveDays(input);
      if (!state.osEnd) throw new LedgerError('MISSING_BASE_DATE', 'days');

      const newEnd = addDays(state.osEnd, days);
      return {
        entry: { ...base, days, previousEndDate: state.osEnd, newEndDate: newEnd },
        nextState: { ...state, osEnd: newEnd },
      };
    }
  }
}

/**
 * Estorno: gera o lançamento que desfaz o efeito de outro, sem apagá-lo.
 *
 * Correção nunca é `UPDATE` nem `DELETE` no razão — o histórico precisa
 * mostrar o erro e a correção (ADR 010).
 */
export function reverseMovement(
  state: ObraLedgerState,
  original: MovementEntry,
  effectiveDate: string,
): MovementResult {
  const valueDelta = original.newValueCents - original.previousValueCents;
  const paidDelta = original.newPaidCents - original.previousPaidCents;
  const glossDelta = original.newGlossCents - original.previousGlossCents;

  const nextState: ObraLedgerState = {
    currentValueCents: state.currentValueCents - valueDelta,
    paidValueCents: state.paidValueCents - paidDelta,
    glossValueCents: state.glossValueCents - glossDelta,
    contractEnd:
      original.type === 'aditivo_prazo_contrato' && original.days
        ? addDays(state.contractEnd ?? original.newEndDate ?? '', -original.days)
        : state.contractEnd,
    osEnd:
      original.type === 'aditivo_prazo_os' && original.days
        ? addDays(state.osEnd ?? original.newEndDate ?? '', -original.days)
        : state.osEnd,
  };

  const entry: MovementEntry = {
    type: original.type,
    amountCents: -original.amountCents,
    days: original.days === null ? null : -original.days,
    effectiveDate,
    previousValueCents: state.currentValueCents,
    newValueCents: nextState.currentValueCents,
    previousPaidCents: state.paidValueCents,
    newPaidCents: nextState.paidValueCents,
    previousGlossCents: state.glossValueCents,
    newGlossCents: nextState.glossValueCents,
    previousEndDate:
      original.type === 'aditivo_prazo_contrato'
        ? state.contractEnd
        : original.type === 'aditivo_prazo_os'
          ? state.osEnd
          : null,
    newEndDate:
      original.type === 'aditivo_prazo_contrato'
        ? nextState.contractEnd
        : original.type === 'aditivo_prazo_os'
          ? nextState.osEnd
          : null,
  };

  return { entry, nextState };
}

/** Descrição curta do efeito, usada em resumo de auditoria e na timeline. */
export function describeMovement(entry: MovementEntry): string {
  const meta = REPORT_TYPE_META[entry.type];
  if (meta.effect === 'none') return meta.label;

  if (entry.days !== null) return `${meta.label}: ${entry.days} dia(s)`;
  return meta.label;
}
